import type { Speaker } from '@/api-gen/api.schemas';
import type { PersonaDto, CreatePersonaDto, UpdatePersonaDto } from '@/api-gen/api.schemas';

/**
 * Voice-related entity with voiceId and voiceName fields
 */
export interface VoiceEntity {
  voiceId?: string | null;
  voiceName?: string | null;
}

/**
 * Resolves a voice from the available voices list using voiceId first, then voiceName as fallback.
 */
export const resolveSelectedVoice = (
  voices: Speaker[],
  entity: VoiceEntity
): Speaker | undefined => {
  if (!voices || voices.length === 0) return undefined;

  if (entity.voiceId) {
    const voiceById = voices.find((voice) => voice.id === entity.voiceId);
    if (voiceById) return voiceById;
  }

  if (entity.voiceName) {
    const voicesByName = voices.filter((voice) => voice.name === entity.voiceName);

    if (voicesByName.length === 0) return undefined;
    if (voicesByName.length === 1) return voicesByName[0];

    const preferredProvider =
      'ttsProvider' in entity ? (entity as { ttsProvider?: string | null }).ttsProvider : null;
    if (preferredProvider) {
      const providerMatch = voicesByName.find((voice) => voice.ttsProvider === preferredProvider);
      if (providerMatch) return providerMatch;
    }

    return voicesByName[0];
  }

  return undefined;
};

/**
 * Maps PersonaDto to form data with proper voiceId handling.
 */
export const mapPersonaDtoToForm = (
  persona: PersonaDto,
  voices: Speaker[]
): { voiceId?: string | null; voiceName?: string | null } => {
  if (persona.voiceId) {
    return {
      voiceId: persona.voiceId,
      voiceName: persona.voiceName
    };
  }

  if (persona.voiceName && voices) {
    const resolvedVoice = resolveSelectedVoice(voices, persona);
    if (resolvedVoice) {
      return {
        voiceId: resolvedVoice.id,
        voiceName: persona.voiceName
      };
    }
  }

  return {
    voiceId: null,
    voiceName: persona.voiceName
  };
};

/**
 * Maps form data to CreatePersonaDto with voiceId and fallback voiceName.
 */
export const mapFormToCreatePersonaDto = (
  formData: any,
  voices: Speaker[]
): Partial<CreatePersonaDto> => {
  const dto: Partial<CreatePersonaDto> = {
    ...formData
  };

  if (formData.voiceId) {
    dto.voiceId = formData.voiceId;

    const voice = voices.find((v) => v.id === formData.voiceId);
    if (voice) {
      dto.voiceName = voice.name;
    }
  } else if (formData.voiceName) {
    dto.voiceId = null;
    dto.voiceName = formData.voiceName;
  }

  return dto;
};

/**
 * Maps form data to UpdatePersonaDto with voiceId and fallback voiceName.
 */
export const mapFormToUpdatePersonaDto = (
  formData: any,
  voices: Speaker[]
): Partial<UpdatePersonaDto> => {
  const dto: Partial<UpdatePersonaDto> = {
    ...formData
  };

  if (formData.voiceId) {
    dto.voiceId = formData.voiceId;

    const voice = voices.find((v) => v.id === formData.voiceId);
    if (voice) {
      dto.voiceName = voice.name;
    }
  } else if (formData.voiceName) {
    dto.voiceId = null;
    dto.voiceName = formData.voiceName;
  }

  return dto;
};

/**
 * Resolves voice name from voiceId for TTS synthesis.
 */
export const resolveVoiceNameForTTS = (
  voices: Speaker[],
  voiceId?: string | null,
  fallbackVoiceName?: string | null
): string | null => {
  if (voiceId && voices) {
    const voice = voices.find((v) => v.id === voiceId);
    if (voice?.name) {
      return voice.name;
    }
  }

  return fallbackVoiceName || null;
};
